import { Controller, useFormContext } from 'react-hook-form';
import {
  AutocompleteRenderInputParams,
  Box,
  FormInputText,
  InputLabel,
  PhoneNumberInput,
  Select,
} from '@hopskipdrive/react-components';
import { variables } from '@rideiq/shared/components/variables';

import { RiderFormValues } from '../../types';

import { RoleField } from './RoleField';

const RELATIONSHIP_OPTIONS = [
  { label: 'Parent or Guardian', value: 'guardian' },
  { label: 'Relative', value: 'relative' },
  { label: 'Social Worker', value: 'social_worker' },
  { label: 'Educational Specialist', value: 'ed_specialist' },
  { label: 'Other', value: 'other' },
];

type ContactRowProps = {
  index: number;
};

export const ContactRow = ({ index }: ContactRowProps) => {
  const { control, formState } = useFormContext<RiderFormValues>();
  const rowErrors = formState.errors.contacts?.[index];
  const isFirst = index === 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        [`@media (max-width: ${variables.breakpoints.tablet - 1}px)`]: { gridTemplateColumns: '1fr' },
        columnGap: '12px',
        rowGap: '12px',
        width: '100%',
      }}
    >
      {/* Row 1 */}
      <Box display="flex" flexDirection="column" gap="12px">
        <InputLabel fieldtype="required" htmlFor={`contacts.${index}.firstName`}>
          First name
        </InputLabel>
        <Controller
          control={control}
          name={`contacts.${index}.firstName` as const}
          render={({ field: { onChange, onBlur, value, name } }) => (
            <FormInputText
              id={name}
              variant="outlined"
              name={name}
              value={value}
              placeholder="Enter first name"
              data-testid={`contacts.${index}.firstName`}
              fullWidth
              error={!!rowErrors?.firstName}
              helperText={rowErrors?.firstName?.message as string | undefined}
              onChange={onChange}
              onBlur={onBlur}
            />
          )}
        />
      </Box>
      <Box display="flex" flexDirection="column" gap="12px">
        <InputLabel fieldtype="required" htmlFor={`contacts.${index}.lastName`}>
          Last name
        </InputLabel>
        <Controller
          control={control}
          name={`contacts.${index}.lastName` as const}
          render={({ field: { onChange, onBlur, value, name } }) => (
            <FormInputText
              id={name}
              variant="outlined"
              name={name}
              value={value}
              placeholder="Enter last name"
              data-testid={`contacts.${index}.lastName`}
              fullWidth
              error={!!rowErrors?.lastName}
              helperText={rowErrors?.lastName?.message as string | undefined}
              onChange={onChange}
              onBlur={onBlur}
            />
          )}
        />
      </Box>

      {/* Row 2 */}
      <Box display="flex" flexDirection="column" gap="12px">
        <InputLabel htmlFor={`contacts.${index}.relationship`}>Relationship</InputLabel>
        <Controller
          control={control}
          name={`contacts.${index}.relationship` as const}
          render={({ field: { value, onChange, onBlur } }) => (
            <Select
              id={`contacts.${index}.relationship`}
              options={RELATIONSHIP_OPTIONS}
              value={value}
              setValue={(v) => onChange(v)}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <FormInputText
                  {...params}
                  inputProps={{ ...params.inputProps, id: `contacts.${index}.relationship` }}
                  variant="outlined"
                  fullWidth
                  placeholder="Select relationship"
                  data-testid={`contacts.${index}.relationship`}
                  onBlur={onBlur}
                />
              )}
            />
          )}
        />
      </Box>
      <RoleField index={index} disabled={isFirst} />

      {/* Row 3 — Phone 50% */}
      <Box display="flex" flexDirection="column" gap="12px">
        <InputLabel fieldtype="required" htmlFor={`contacts.${index}.phoneNumber`}>
          Phone number
        </InputLabel>
        <Controller
          control={control}
          name={`contacts.${index}.phoneNumber` as const}
          render={({ field: { onChange, onBlur, value, name } }) => (
            <PhoneNumberInput
              variant="outlined"
              name={name}
              id={name}
              placeholder="Enter phone number"
              data-testid={`contacts.${index}.phoneNumber`}
              onValueChange={(v) => onChange(v)}
              onBlur={onBlur}
              initialValue={value}
              error={!!rowErrors?.phoneNumber}
              helperText={rowErrors?.phoneNumber?.message as string | undefined}
            />
          )}
        />
      </Box>
      {/* Empty col-2 cell — phone is 50% per design */}
      <Box
        sx={{ display: 'block', [`@media (max-width: ${variables.breakpoints.tablet - 1}px)`]: { display: 'none' } }}
      />

      {/* Row 4 — Email full width */}
      <Box display="flex" flexDirection="column" gap="12px" sx={{ gridColumn: '1 / -1' }}>
        <InputLabel htmlFor={`contacts.${index}.email`}>Email</InputLabel>
        <Controller
          control={control}
          name={`contacts.${index}.email` as const}
          render={({ field: { onChange, onBlur, value, name } }) => (
            <FormInputText
              id={name}
              name={name}
              variant="outlined"
              fullWidth
              placeholder="Enter email"
              data-testid={`contacts.${index}.email`}
              value={value}
              error={!!rowErrors?.email}
              helperText={rowErrors?.email?.message as string | undefined}
              onChange={onChange}
              onBlur={onBlur}
            />
          )}
        />
      </Box>
    </Box>
  );
};
